import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    IconButton,
    Autocomplete,
    CardContent,
    InputAdornment,
    Card,
} from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import dayjs, { Dayjs } from 'dayjs';
import { Loader } from '../../components/loader/Loader';
import CustomDataGrid from '../../components/dms_common/CustomDataGrid';
import './interfaceSyncMonitor.css';
import { openPopup } from '../../stateManagement/actions/popupActions';
import { changeEntryStatus, fetchInterfaceData, syncSingleRecord } from './InterfaceSyncService';

const rectangularInputSx = {
    minWidth: 90,
    background: "#fafbfc",
    borderRadius: "6px",

    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        background: "#fafbfc",
        paddingRight: "36px !important",
        height: "40px",
    },

    "& .MuiInputBase-input": {
        padding: "6px 8px !important",
        // paddingRight: "4px !important", 
        display: "flex",
        alignItems: "center",
    },

    "& .MuiSelect-select": {
        padding: "6px 8px !important",
    },

    "& .MuiSvgIcon-root": {
        fontSize: 20,
        right: 6,
    },
    "& .MuiAutocomplete-clearIndicator": {
        padding: 0,
        marginRight: "-4px",
    },

    "& .MuiAutocomplete-popupIndicator": {
        padding: 0,
        marginLeft: "-4px",
    },

    "& .MuiAutocomplete-endAdornment": {
        right: 2,
        gap: "2px",
        top: "50%",
        transform: "translateY(-50%)",
        display: "flex",
        alignItems: "center",
    },

    "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
        fontSize: 20,
    },
};

const rectangularOuterBox = {
    background: "#fafbfc",
    borderRadius: "6px",
    border: "1px solid #c4c4c4",
    height: "40px",
    padding: "6px 8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
};

interface InterfaceSyncModel {
    id: string;
    depot: string;
    uniqueSalesRepId: string;
    route: string;
    salesmanType: string;
    closingDate: string;     
    receivingDate: string;   
    status: string;
    actualStatus: string;
    referenceNumbers: string[];
}


export function InterfaceSync() {
    const [loading, setLoading] = useState<boolean>(false);
    const [interfaceType, setInterfaceType] = useState<string | null>(null);
    const [interfaceList, setInterfaceList] = useState<string[]>([]);
    const [salesRoute, setSalesRoute] = useState<string | null>(null);
    const [salesRouteList, setSalesRouteList] = useState<string[]>([]);
    const [depot, setDepot] = useState<string | null>(null);
    const [depotList, setDepotList] = useState<string[]>([]);
    const [status, setStatus] = useState<string | null>(null);
    const [tempStatus, setTempStatus] = useState<string | null>(null);
    const [statusList, setStatusList] = useState<string[]>([]);
    const [fromDate, setFromDate] = useState<Dayjs | null>();
    const [toDate, setToDate] = useState<Dayjs | null>();
    const [openFromPicker, setOpenFromPicker] = useState(false);
    const [openToPicker, setOpenToPicker] = useState(false);
    const fromDateRef = React.useRef(null);
    const toDateRef = React.useRef(null);
    const [originalRows, setOriginalRows] = useState<any[]>([]);
    const originalRowsRef = React.useRef<any []>([]);
    const [rows, setRows] = useState<InterfaceSyncModel[]>([]);
    const rowsRef = React.useRef<InterfaceSyncModel []>([]);
    const isRowsVisible = useRef<boolean>(false);
    const [checkedIdArray, setCheckedIdArray] = useState<string[]>([]);
    const [checkedRows, setCheckedRows] = useState<InterfaceSyncModel[]>([]);
    const [checkedCustomIdArray, setCheckedCustomIdArray] = useState<string>("");
    const [searchText, setSearchText] = useState("");
    const [lastUpdatedOn, setLastUpdatedOn] = useState<string>(dayjs().format("DD-MM-YYYY HH:mm:ss"));
    const [filteredMetadata, setFilteredMetadata] = useState<{ columns: any[]; Actions: string[] }>({columns: [], Actions: []});
    const [isSync, setIsSync] = useState<boolean>(true);
    const isSyncRef = useRef<boolean>(true);

    const metadata = {
        columns: [
            {
                headerName: "Depot",
                headerParts: { top: "Depot" },
                mappedValue: "depot",
                width: 170,
                headerAlign: "left",
                align: "left",
                truncateText: true,
                keyAccountShow: true,
            },
            {
                headerName: "Sales Rep ID",
                headerParts: { top: "Sales Rep ID" },
                mappedValue: "uniqueSalesRepId",
                width: 180,
                headerAlign: "left",
                align: "left",
                truncateText: true,
            },
            {
                headerName: "Sales Rep Route",
                mappedValue: "route",
                width: 170,
                headerAlign: "left",
                align: "left",
                type: "order",
            },
            {
                headerName: "Salesman Type",
                mappedValue: "salesmanType",
                width: 170,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Closing Date",
                mappedValue: "closingDate",
                width: 170,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Receiving Date",
                mappedValue: "receivingDate",
                width: 180,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Upload Status",
                mappedValue: "status",
                width: 180,
                headerAlign: "left",
                align: "left",
            },
        ],
        Actions: [],
    };

    const fetchData = async () => {
        if(!interfaceType) return;
        setLoading(true);

        try {
            const result = await fetchInterfaceData(interfaceType, fromDate?.toString() ?? "", toDate?.toString() ?? "");
            if(!result) throw Error("Unable to fetch data");
            setOriginalRows(result); 
            originalRowsRef.current = result;
            if(isRowsVisible.current){ setRows(result);
                rowsRef.current = result;
            }

            const salesRoutes: string[] = Array.from(
                new Set(
                    result
                        .map((o: any) => o.beat)
                        .filter((v: any): v is string => Boolean(v))
                )
            );
            // setSalesRouteList(salesRoutes);

            const depots: string[] = Array.from(
                new Set(
                    result
                        .map((o: any) => o.depot)
                        .filter((v: any): v is string => Boolean(v))
                )
            );
            setDepotList(depots);

            const statuses: string[] = Array.from(
                new Set(
                    result
                        .map((o: any) => o.status)
                        .filter((v: any): v is string => Boolean(v))
                )
            );
            // setStatusList(statuses);
            if(isRowsVisible.current){
                // setSalesRouteList(salesRoutes);
                // setStatusList(statuses);
                onDepotSelect(depot ?? "");
                onRouteSelect(salesRoute ?? "");
                setStatus(status);
                setTempStatus(status);
                handleApplyFilters();
                handleSearch(searchText);
            }
        } catch {
            openPopup("Error", "Failed to fetch data");
        } finally {
            updateLastUpdatedOn();
            setLoading(false);
        }
    };

    const handleApplyFilters = () => {
        if (!fromDate || !toDate) {
            openPopup("Error", "Please select Date Range");
            return;
        }

        if (!interfaceType) {
            openPopup("Error", "Please select an Interface.");
            return;
        }

        if (interfaceType.toLowerCase() !== "payments" && !salesRoute && !depot) {
            openPopup("Error", "Please select either Sales Route or Depot");
            return;
        }

        if(interfaceType.toLowerCase() === "payments" && !depot){
            openPopup("Error", "Please select Depot");
            return;
        }

        if (!tempStatus) {
            openPopup("Error", "Please select a Status.");
            return;
        }

        setStatus(tempStatus);

        let filtered = [...originalRowsRef.current];

        if (salesRoute) {
            filtered = filtered.filter(r =>
                r.route === salesRoute
            );
        }

        if (depot) {
            filtered = filtered.filter(r =>
                r.depot === depot
            );
        }

        if (tempStatus) {
            filtered = filtered.filter(r =>
                r.status?.toLowerCase() === tempStatus.toLowerCase()
            );
        }

        // if (fromDate || toDate) {
        //     filtered = filtered.filter(row => {
        //         const rowDate = dayjs(row.creationTime, "YYYY-MM-DD HH:mm:ss");

        //         const start = fromDate ? dayjs(fromDate).startOf("day") : null;
        //         const end = toDate ? dayjs(toDate).endOf("day") : null;

        //         // Case 1: only fromDate
        //         if (start && !end) {
        //             return rowDate.isSame(start, "day") || rowDate.isAfter(start);
        //         }

        //         // Case 2: only toDate
        //         if (!start && end) {
        //             return rowDate.isSame(end, "day") || rowDate.isBefore(end);
        //         }

        //         // Case 3: both fromDate and toDate
        //         if (start && end) {
        //             return (
        //                 rowDate.isSame(start, "day") ||
        //                 rowDate.isSame(end, "day") ||
        //                 (rowDate.isAfter(start) && rowDate.isBefore(end))
        //             );
        //         }

        //         return true;
        //     });
        // }


        setRows(filtered);
        rowsRef.current = filtered;
        isRowsVisible.current = true;
        setCheckedIdArray([]);
        setCheckedCustomIdArray("");
        setCheckedRows([]);
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        if(!isRowsVisible.current) return;
        if (!value.trim()) {
            setRows(originalRowsRef.current);
            rowsRef.current = originalRowsRef.current;
            handleApplyFilters();
            // setRows(rows);
            return;
        }

        const lower = value.toLowerCase();  

        handleApplyFilters();

        const filtered = rowsRef.current.filter(r =>
            (r.depot?.toLowerCase().includes(lower)) ||
            (r.uniqueSalesRepId?.toLowerCase().includes(lower)) ||
            (r.route?.toLowerCase().includes(lower)) ||
            (r.salesmanType?.toLowerCase().includes(lower)) ||
            (r.closingDate?.toLowerCase().includes(lower)) ||
            (r.receivingDate?.toLowerCase().includes(lower)) ||
            (r.status?.toLowerCase().includes(lower))
        );

        setRows(filtered);
        rowsRef.current = filtered;
        isRowsVisible.current = true;
    };

    const onDepotSelect = (value: string) => {
        if (!fromDate || !toDate) {
            openPopup("Error", "Please select Date range first");
            return;
        }
        setDepot(value || "");
        setSalesRoute("");
        setStatus("");
        setTempStatus("");

        let filtered = [...originalRowsRef.current];
        
        filtered = filtered.filter(r =>
            r.depot.toLowerCase() === value.toLowerCase()
        );
        
        const salesRoutes: string[] = Array.from(
            new Set(
                filtered
                    .map((o: any) => o.beat)
                    .filter((v: any): v is string => Boolean(v))
            )
        );
        setSalesRouteList(salesRoutes);

        const statuses: string[] = Array.from(
            new Set(
                filtered
                    .map((o: any) => o.status)
                    .filter((v: any): v is string => Boolean(v))
            )
        );
        setStatusList(statuses);
        isRowsVisible.current = false;
    }

    const onRouteSelect = (value: string) => {
        if (!fromDate || !toDate) {
            openPopup("Error", "Please select Date range first");
            return;
        }

        if(value === ""){
            onDepotSelect(depot ?? "");
            return;
        }
        setSalesRoute(value || "");
        setStatus("");
        setTempStatus("");

        let filtered = [...originalRowsRef.current];
        filtered = filtered.filter(r =>
            r.depot.toLowerCase() === depot?.toLowerCase()
        );
        filtered = filtered.filter(r =>
            r.route.toLowerCase() === value?.toLowerCase()
        );

        const statuses: string[] = Array.from(
            new Set(
                filtered
                    .map((o: any) => o.status)
                    .filter((v: any): v is string => Boolean(v))
            )
        );
        setStatusList(statuses);
        isRowsVisible.current = false;
    }

    const updateCheckedIdArray = (selectedIds: string[]) => {
        const selectedRows = selectedIds
            .map(id => rowsRef.current.find(r => r.id === id) || null)
            .filter((row): row is any => row !== null);

        const uniqueRows = Array.from(
            new Map(selectedRows.map(row => [row.id, row])).values()
        );

        let customIdString = "";

        if (interfaceType?.toLowerCase() == "orders" && (searchText !== "" || uniqueRows.length !== rowsRef.current.length)) {
            customIdString = uniqueRows
                .map(row => {
                    const salesRepId = row.uniqueSalesRepId;
                    const creationDate = row.receivingDate;
                    return `${salesRepId}:${creationDate}`;
                })
                .join(",");
        }

        if (interfaceType?.toLowerCase() == "payments" && (searchText !== "" || uniqueRows.length !== rowsRef.current.length)) {
            customIdString = uniqueRows
                .flatMap(row => row.referenceNumbers ?? [])
                .join(",");
        }

        setCheckedIdArray(selectedIds);   
        setCheckedRows(uniqueRows);  
        setCheckedCustomIdArray(customIdString);   
    };


    const handleSync = async () => {
        if (!interfaceType) return;
        if (checkedRows.length === 0) {
            openPopup("Alert", "Please select at least one row to sync.");
            return;
        }
        setLoading(true);
        try {
            let statusToSent = status;
            if(interfaceType.toLowerCase() == "orders"){
                if(status?.toLowerCase() == "success") statusToSent = "SENTTOBASIS";
                else {
                    const uniqueStatuses = Array.from(
                        new Set(
                            checkedRows
                                .map(row => row.actualStatus)
                                .filter(Boolean) 
                        )
                    );
                    statusToSent = uniqueStatuses.join(",");
                }
            }else{
                const uniqueStatuses = Array.from(
                        new Set(
                            checkedRows
                                .map(row => row.actualStatus)
                                .filter(Boolean) 
                        )
                    );
                statusToSent = uniqueStatuses.join(",");
            }

            const mergedEntries = Array.from(
                new Set(
                    checkedRows.flatMap(row => row.referenceNumbers || [])
                )
            );
            await changeEntryStatus(interfaceType, mergedEntries);
            const result = await syncSingleRecord(interfaceType, fromDate?.toString() ?? "", toDate?.toString() ?? "", salesRoute ?? "", depot ?? "", statusToSent ?? "", checkedCustomIdArray ?? "", isSyncRef.current);
            if (!result.success) {
                openPopup("Error", "Records sync failed");
            }else{
                openPopup("Success", "Records sync in progress");
            }
        } catch (err) {
            openPopup("Error", "Records sync failed");
        } finally {
            await fetchData();

            setCheckedIdArray([]);
            setCheckedRows([]);
            setCheckedCustomIdArray("");   
            setLoading(false);
        }
    };

    const updateLastUpdatedOn = () => {
        setLastUpdatedOn(dayjs().format("DD-MM-YYYY HH:mm:ss"));
    };

    useEffect(() => {
        // if (!interfaceType) return;
        setDepot("");
        setTempStatus("");
        setStatus("");
        setSalesRoute("");
        setRows([]);
        rowsRef.current = [];
        setOriginalRows([]);
        originalRowsRef.current = [];
        setDepotList([]);
        setSalesRouteList([]);
        setStatusList([]);
        isRowsVisible.current = false;
        setSearchText("");
        fetchData();
    }, [interfaceType]);

    useEffect(() => {
        if (fromDate && toDate) {
            setInterfaceList(["Orders", "Payments"]);
            // setInterfaceList(["Orders", "Payments", "Settlements"]);
            if(interfaceType){
                // isRowsVisible.current = true;
                fetchData();
                // handleApplyFilters();
            }
        } else {
            setInterfaceList([]);
            setInterfaceType("");
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        const updated = {
            ...metadata,
            columns: metadata.columns.filter(col =>
                interfaceType?.toLowerCase() === "payments"
                    ? col.mappedValue !== "route"
                    : true
            )
        }

        setFilteredMetadata(updated);
    }, [interfaceType]); 

    return (
        <Box padding={2} bgcolor={"white"} height={"100%"} width={"100%"}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Loader />
                </Box>
            ) :
                (
                    <div className="InterfaceSyncContainer">
                        <Card
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                boxShadow: "0 1px 1px rgba(0, 0, 0, 0.1)",
                                border: "1px solid rgba(126, 121, 121, 0.1)",
                                width: "100%"
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    mb={3}
                                    gap={1}
                                >
                                    <Box display={"flex"} alignItems={"center"} gap={2}>
                                        <Typography variant="h5" fontWeight={700}>
                                            Interface Sync Monitor
                                        </Typography>

                                        <Typography variant="body2" color="gray">
                                            Last updated on:{" "}{lastUpdatedOn || ""}
                                        </Typography>
                                    </Box>


                                    <Box display="flex" justifyContent="flex-end">
                                        <TextField
                                            placeholder="Search"
                                            size="small"
                                            value={searchText}
                                            onChange={(e) => handleSearch(e.target.value)}
                                            sx={{ width: 260 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <IconButton size="small">
                                                            <SearchIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Grid container spacing={1.5} alignItems="center" width={"100%"}>
                                    <Grid item xs={12} sm={6} md={3} lg={2.3}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography sx={{ whiteSpace: "nowrap", fontSize: 15 }}>
                                                From Date
                                            </Typography>

                                            {/* Custom UI Box */}
                                            <Box
                                                ref={fromDateRef}
                                                onClick={() => setOpenFromPicker(true)}
                                                sx={{
                                                    ...rectangularOuterBox,
                                                    minWidth: 160,
                                                }}
                                            >
                                                <CalendarMonthOutlinedIcon fontSize="small" />

                                                <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
                                                    {fromDate ? dayjs(fromDate).format("DD-MM-YYYY") : "Select"}
                                                </Typography>
                                            </Box>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    open={openFromPicker}
                                                    onOpen={() => setOpenFromPicker(true)}
                                                    onClose={() => setOpenFromPicker(false)}
                                                    inputFormat="DD-MM-YYYY"
                                                    label="From Date"
                                                    maxDate={toDate ?? dayjs()}
                                                    value={fromDate}
                                                    onChange={(value) => {
                                                        if (value) setFromDate((dayjs(value).startOf("day")));
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} sx={{ display: "none" }} />
                                                    )}
                                                    PopperProps={{
                                                        anchorEl: fromDateRef.current,
                                                        placement: "bottom-start",
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={2.3}>
                                        <Box display="flex" alignItems="center" gap={1} padding={1}>

                                            <Typography sx={{ whiteSpace: "nowrap", fontSize: 15 }}>
                                                To Date
                                            </Typography>

                                            {/* Custom UI Box */}
                                            <Box
                                                ref={toDateRef}
                                                onClick={() => setOpenToPicker(true)}
                                                sx={{
                                                    ...rectangularOuterBox,
                                                    minWidth: 160,
                                                }}
                                            >
                                                <CalendarMonthOutlinedIcon fontSize="small" />

                                                <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
                                                    {toDate ? dayjs(toDate).format("DD-MM-YYYY") : "Select"}
                                                </Typography>
                                            </Box>

                                            {/* Hidden Date Picker */}
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    open={openToPicker}
                                                    onOpen={() => setOpenToPicker(true)}
                                                    onClose={() => setOpenToPicker(false)}
                                                    inputFormat="DD-MM-YYYY"
                                                    label="To Date"
                                                    minDate={fromDate}
                                                    maxDate={dayjs()}
                                                    value={toDate}
                                                    onChange={(value) => {
                                                        if (value) setToDate((dayjs(value).endOf("day")));
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} sx={{ display: "none" }} />
                                                    )}
                                                    PopperProps={{
                                                        anchorEl: toDateRef.current,
                                                        placement: "bottom-start",
                                                    }}
                                                />
                                            </LocalizationProvider>

                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={2} md={2} lg={2}>
                                        <Autocomplete
                                            options={interfaceList}
                                            value={interfaceType}
                                            onOpen={() => {
                                                if (!fromDate || !toDate) {
                                                    openPopup("Error", "Please select Date range first");
                                                }
                                            }}
                                            onChange={(_, newValue) => {
                                                if (!fromDate || !toDate) {
                                                    openPopup("Error", "Please select Date range first");
                                                    return;
                                                }
                                                setInterfaceType(newValue || "");
                                            }}
                                            clearOnEscape
                                            // disableClearable
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    style={{
                                                        // fontSize: "0.85rem",
                                                        whiteSpace: "normal",      // allow wrapping
                                                        wordBreak: "break-word",
                                                        overflow: "visible",
                                                        textOverflow: "unset",
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                    {option}
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Interface"
                                                    size="small"
                                                    sx={rectangularInputSx}
                                                />
                                            )}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3} md={2} lg={1.3}>
                                        <Autocomplete
                                            options={depotList}
                                            value={depot}
                                            onOpen={() => {
                                                if (!fromDate || !toDate) {
                                                    openPopup("Error", "Please select Date range first");
                                                }
                                            }}
                                            onChange={(_, newValue) => {
                                                // if (!fromDate || !toDate) {
                                                //     openPopup("Error", "Please select Date range first");
                                                //     return;
                                                // }
                                                // setDepot(newValue || "");
                                                onDepotSelect(newValue ?? "");
                                            }}
                                            clearOnEscape
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    style={{
                                                        // fontSize: "0.85rem",
                                                        whiteSpace: "normal",      // allow wrapping
                                                        wordBreak: "break-word",
                                                        overflow: "visible",
                                                        textOverflow: "unset",
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                    {option}
                                                </li>
                                            )}
                                            // disableClearable
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Depot"
                                                    size="small"
                                                    sx={rectangularInputSx}
                                                />
                                            )}
                                            fullWidth
                                        />
                                    </Grid>

                                    {interfaceType?.toLowerCase() !== "payments" && <Grid item xs={12} sm={3} md={2} lg={2}>
                                        <Autocomplete
                                            options={salesRouteList}
                                            value={salesRoute}
                                            onOpen={() => {
                                                if (!fromDate || !toDate) {
                                                    openPopup("Error", "Please select Date range first");
                                                }
                                            }}
                                            onChange={(_, newValue) => {
                                                // if (!fromDate || !toDate) {
                                                //     openPopup("Error", "Please select Date range first");
                                                //     return;
                                                // }
                                                // setSalesRoute(newValue || "");
                                                onRouteSelect(newValue ?? "");
                                            }}
                                            clearOnEscape
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    style={{
                                                        // fontSize: "0.85rem",
                                                        whiteSpace: "normal",      // allow wrapping
                                                        wordBreak: "break-word",
                                                        overflow: "visible",
                                                        textOverflow: "unset",
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                    {option}
                                                </li>
                                            )}
                                            // disableClearable
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Sales Route"
                                                    size="small"
                                                    sx={rectangularInputSx}
                                                />
                                            )}
                                            fullWidth
                                        />
                                    </Grid>}

                                    <Grid item xs={12} sm={3} md={2} lg={1.3}>
                                        <Autocomplete
                                            options={statusList}
                                            value={tempStatus}
                                            onOpen={() => {
                                                if (!fromDate || !toDate) {
                                                    openPopup("Error", "Please select Date range first");
                                                }
                                            }}
                                            onChange={(_, newValue) => {
                                                if (!fromDate || !toDate) {
                                                    openPopup("Error", "Please select Date range first");
                                                    return;
                                                }
                                                setTempStatus(newValue || "");
                                                isRowsVisible.current = false;
                                            }}
                                            clearOnEscape
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    style={{
                                                        // fontSize: "0.85rem",
                                                        whiteSpace: "normal",      // allow wrapping
                                                        wordBreak: "break-word",
                                                        overflow: "visible",
                                                        textOverflow: "unset",
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                    {option}
                                                </li>
                                            )}
                                            // disableClearable
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Status"
                                                    size="small"
                                                    sx={rectangularInputSx}
                                                />
                                            )}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3} md={1} lg={0.8}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            sx={{ height: 40, borderRadius: 2, backgroundColor: "#23c6b2" }}
                                            onClick={handleApplyFilters}
                                        >
                                            Apply
                                        </Button>
                                    </Grid>
                                </Grid>

                            </CardContent>
                        </Card>

                        <CustomDataGrid
                            rows={isRowsVisible.current ? rowsRef.current : []}
                            columnsMetaData={filteredMetadata}
                            isCheckboxSelection={true}
                            tableWidth={0.95}
                            checkedIdArray={checkedIdArray}
                            setCheckedIdArray={(ids: any) => updateCheckedIdArray(ids)}
                        />

                        <Box display="flex" justifyContent="flex-end" gap={1} paddingTop={2}>
                            <Button
                                variant="outlined"
                                startIcon={<AutorenewIcon />}
                                sx={{ height: 40, borderRadius: 2, color: "black", borderColor: "#808080" }}
                                onClick={() => {
                                    setIsSync(true);
                                    isSyncRef.current = true;
                                    handleSync();
                                }}
                                disabled={
                                    !isRowsVisible.current ||
                                    rowsRef.current.length <= 0 ||
                                    !status ||                     
                                    (status?.toLowerCase() !== "pending" &&
                                    status?.toLowerCase() !== "on_hold" )
                                }
                            >
                                Sync
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<RefreshRoundedIcon />}
                                sx={{ height: 40, borderRadius: 2, color: "black", borderColor: "#808080" }}
                                onClick={() => {
                                    setIsSync(false);
                                    isSyncRef.current = false;
                                    handleSync();
                                }}
                                disabled={
                                    !isRowsVisible.current ||
                                    rowsRef.current.length <= 0 ||
                                    !status ||                     
                                    status?.toLowerCase() === "pending" ||
                                    status?.toLowerCase() === "inprogress" ||
                                    status?.toLowerCase() === "on_hold"
                                }
                            >
                                Re-Sync
                            </Button>

                            {/* <Button
                                variant="contained"
                                startIcon={<AutorenewIcon />}
                                sx={{ height: 40, borderRadius: 2, backgroundColor: "#23c6b2" }}
                                onClick={handleSyncAll}
                            >
                                Sync All
                            </Button> */}
                        </Box>

                    </div>
                )
            }
        </Box>
    );
} 